/**
 * ClauseRow — one clause of the version being reviewed: path, heading,
 * topic (inferred, §9.3), its carried-language proposal if any (session 4
 * FACTS 6), its clause-scoped flags (FlagList, FACTS 4) and similarity
 * matches (SimilarList, FACTS 5), and its decision control (DecisionBar,
 * FACTS 7) — or "Not reviewed yet" when `review` is `null`.
 *
 * §13.7's second Verify assertion ("the position renders beside a flagged
 * clause with its basis") is asserted by querying WITHIN this row's own
 * `data-testid` subtree, not by a page-wide text search — everything a
 * clause needs lives inside `ctr-clause-<id>`.
 */
import type { ClauseCarriedFrom, ClauseDecision, ClauseRow as ClauseRowData } from '../../api/clauses';
import { topicLabel } from './topics';
import { FlagList } from './FlagList';
import { SimilarList } from './SimilarList';
import { DecisionBar } from './DecisionBar';

interface ClauseRowProps {
  clause: ClauseRowData;
  deciding: boolean;
  onDecide: (reviewId: string, decision: ClauseDecision) => void;
}

function CarriedFromBlock({
  clauseText,
  carriedFrom,
  decision,
}: {
  clauseText: string;
  carriedFrom: ClauseCarriedFrom;
  decision: ClauseDecision | null;
}) {
  return (
    <div className="mt-2 grid grid-cols-1 gap-2 rounded-lg border border-primary-200 bg-primary-50 p-2 text-xs dark:border-primary-800 dark:bg-primary-900/20 sm:grid-cols-2">
      <div className="min-w-0">
        <p className="font-medium text-gray-600 dark:text-gray-300">Current text</p>
        <p className="whitespace-pre-line text-gray-700 dark:text-gray-300">{clauseText}</p>
      </div>
      <div className="min-w-0">
        <p className="font-medium text-gray-600 dark:text-gray-300">Proposed (carried)</p>
        <p className="whitespace-pre-line text-gray-700 dark:text-gray-300">{carriedFrom.proposed_text}</p>
      </div>
      <div
        className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400 sm:col-span-2"
        title={`carried at ${carriedFrom.carried_at}`}
      >
        <span>
          From study {carriedFrom.study_id}, version {carriedFrom.version_id}, clause{' '}
          {carriedFrom.clause_path ?? '—'}, agreed {carriedFrom.date ?? 'unknown date'}.
        </span>
        {decision === null && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
            Proposed, not accepted
          </span>
        )}
      </div>
    </div>
  );
}

export function ClauseRow({ clause, deciding, onDecide }: ClauseRowProps) {
  const review = clause.review;
  const clauseFlags = (review?.flags ?? []).filter((f) => f.scope !== 'document');

  return (
    <div
      data-testid={`ctr-clause-${clause.id}`}
      className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span className="tabular-nums">{clause.path ?? '—'}</span>
            {review && (
              <span
                title={`topic is inferred (§9.3): ${review.topic ?? 'unassigned'}`}
                className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300"
              >
                {topicLabel(review.topic)}
              </span>
            )}
          </div>
          <h4 className="mt-0.5 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
            {clause.heading ?? 'Untitled clause'}
          </h4>
        </div>

        {review ? (
          <DecisionBar review={review} deciding={deciding} onDecide={onDecide} />
        ) : (
          <span className="text-xs text-gray-400 dark:text-gray-500">Not reviewed yet</span>
        )}
      </div>

      {review?.carried_from && (
        <CarriedFromBlock clauseText={clause.text} carriedFrom={review.carried_from} decision={review.decision} />
      )}

      {review && <FlagList flags={clauseFlags} clauseId={clause.id} />}
      {review && <SimilarList similar={review.similar} clauseId={clause.id} />}
    </div>
  );
}

export default ClauseRow;
